import {
    faComputerSpeaker,
    faLightbulb,
    faMicrophone,
    faSlidersUp,
    faSpeaker,
    faTurntable,
    faWaveform
} from "@fortawesome/pro-solid-svg-icons";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {isNumber} from "lodash-es";
import {PRODUCT_PAGE} from "../Routes";

export type ProductValue = {
    producent?: string
    count?: number
    category?: string[]
    type?: string
    rentPrice?: number | string
    specialName?: boolean
    isSpecialPrice?: boolean
    image?: string
    imageGallery?: string[]
}

export type ProductEntry = {
    /** Raw key from the remote config — also the lookup id. */
    id: string
    value: ProductValue
}

export const PRODUCT_CATEGORIES = {
    DJ: 'DJ',
    SCENE: 'SCENE'
}

/** Route slug ↔ category used by the offer list tabs. */
export const CATEGORY_BY_SLUG: Record<string, string | undefined> = {
    'Wybor': undefined,
    'Sprzet-DJ': PRODUCT_CATEGORIES.DJ,
    'Sprzet-Sceniczny': PRODUCT_CATEGORIES.SCENE
}

export const SLUG_BY_CATEGORY: Record<string, string> = {
    [PRODUCT_CATEGORIES.DJ]: 'Sprzet-DJ',
    [PRODUCT_CATEGORIES.SCENE]: 'Sprzet-Sceniczny'
}

export const getProductIcon = (type?: string): IconProp => {
    switch (type) {
        case 'Console':
            return faTurntable
        case 'Mixer':
            return faSlidersUp
        case 'Setup':
            return faComputerSpeaker
        case 'Speakers':
            return faSpeaker
        case 'MicroPhone':
            return faMicrophone
        case 'Light':
            return faLightbulb
        default:
            return faWaveform
    }
}

/** Products flagged with `specialName` carry their label in the translations. */
export const getProductName = (id: string, value: ProductValue, t: (key: string) => string) => (
    value?.specialName ? t(`lbl_${id}`) : id.split('_').join(' ')
)

export const hasNumericPrice = (rentPrice?: number | string): rentPrice is number => isNumber(rentPrice)

export const getProductPath = (id: string) => `${PRODUCT_PAGE}/${encodeURIComponent(id)}`

/** Sorted, filtered view over the remote product map. */
export const toProductList = (products: Record<string, ProductValue> | undefined): ProductEntry[] => (
    Object.keys(products ?? {}).map((id) => ({id, value: (products as any)[id]}))
)

export const filterByCategory = (list: ProductEntry[], category?: string) => (
    !category ? list : list.filter(({value}) => (value?.category ?? []).includes(category))
)

export const countInCategory = (list: ProductEntry[], category?: string) => filterByCategory(list, category).length

export const cheapestPrice = (list: ProductEntry[]) => {
    const prices = list
        .map(({value}) => value?.rentPrice)
        .filter(hasNumericPrice)
    return prices.length ? Math.min(...prices) : undefined
}

export const uniqueProducers = (list: ProductEntry[]) => (
    Array.from(new Set(list.map(({value}) => value?.producent).filter(Boolean))) as string[]
)
